import { Loader2, Trash2 } from "lucide-react";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { useUpdateAlertRule, useDeleteAlertRule } from "@/hooks/use-alerts";
import { useUIStore } from "@/stores/ui-store";
import type { AlertRule } from "@/api/types";

const CATEGORY_LABELS: Record<string, string> = {
  overdue_query: "Overdue Query",
  enrollment_behind_target: "Enrollment Behind Target",
  site_inactive: "Site Inactive",
  missing_forms: "Missing Forms",
  visit_window_exceeded: "Visit Window Exceeded",
  screening_failure_spike: "Screening Failure Spike",
};

export function AlertRuleInspector({ data }: { data: unknown }) {
  const rule = data as AlertRule;
  const updateRule = useUpdateAlertRule();
  const deleteRule = useDeleteAlertRule();
  const { closeInspector } = useUIStore();

  const handleToggle = () => {
    updateRule.mutate({ ruleId: rule.id, enabled: !rule.enabled });
  };

  const handleDelete = () => {
    if (confirm("Delete this alert rule?")) {
      deleteRule.mutate({ ruleId: rule.id }, {
        onSuccess: () => closeInspector(),
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground">{rule.name}</h3>
        <div className="mt-2">
          <StatusBadge status={rule.enabled ? "active" : "inactive"} />
        </div>
      </div>

      <div className="space-y-2">
        <Detail label="Category" value={CATEGORY_LABELS[rule.category] ?? rule.category} />
        <Detail
          label="Studies"
          value={rule.studyIds.length === 0 ? "All studies" : `${rule.studyIds.length} studies`}
        />
        <Detail
          label="Notify Roles"
          value={rule.notifyRoles.length === 0 ? "None" : rule.notifyRoles.join(", ")}
        />
      </div>

      {Object.keys(rule.thresholds).length > 0 && (
        <div>
          <h4 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Thresholds
          </h4>
          <div className="rounded-lg border border-border divide-y divide-border">
            {Object.entries(rule.thresholds).map(([key, value]) => (
              <div key={key} className="flex justify-between gap-4 px-3 py-2">
                <span className="text-xs font-medium text-muted-foreground">{key.replace(/_/g, " ")}</span>
                <span className="text-xs text-foreground">{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 border-t border-border pt-4">
        <button
          onClick={handleToggle}
          disabled={updateRule.isPending}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {updateRule.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {rule.enabled ? "Disable" : "Enable"}
        </button>
        <button
          onClick={handleDelete}
          disabled={deleteRule.isPending}
          className="inline-flex items-center gap-1.5 rounded-md bg-destructive px-3 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
        >
          {deleteRule.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
          Delete
        </button>
      </div>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <p className="text-sm text-foreground">{value}</p>
    </div>
  );
}
